/**
 * Shared source classification utilities.
 * Used by citation-bubble.tsx, message-sources.tsx, and chat.ts.
 */
import { sourceColor, sourceFullName } from '#app/utils/search-lib'

export type SourceClassification = 'im-only' | 'mixed' | 'external-only'

export const SOURCE_NAMES: Record<string, string> = {
	'internetmedicin.se': 'Internetmedicin',
	'cancercentrum.se': 'RCC',
	'1177.se': '1177',
	'vgregion.se': 'RMR VGR',
	'viss.nu': 'VISS',
	'socialstyrelsen.se': 'Socialstyrelsen',
	'fass.se': 'FASS',
	'lakemedelsverket.se': 'Läkemedelsverket',
	'folkhalsomyndigheten.se': 'Folkhälsomyndigheten',
	'lakemedelsboken.se': 'Läkemedelsboken',
	'janusinfo.se': 'Janusinfo',
	'sfog.se': 'SFOG',
	'svenskgastroenterologi.se': 'SGF',
	'slmf.se': 'SLMF',
	'sfai.se': 'SFAI',
	'sfhem.se': 'SFHem',
	'ssdv.se': 'SSDV',
	'sfmg.se': 'SFMG',
	'ssth.se': 'SSTH',
	'snema.se': 'Svenska Neuromuskulära Arbetsgruppen',
	'sffa.se': 'Svenska föreningen för Allergologi',
	'swemodis.se': 'Swedish Movement Disorder Society',
	'svos.se': 'Svenska Osteoporossällskapet',
	'urologi.org': 'Svensk Urologisk Förening',
	'svenskonh.se': 'Svensk ONH-förening',
	'endokrinologforeningen.se': 'Svensk Endokrinologförening',
	'svenskberoendemedicin.se': 'Svensk Förening för Beroendemedicin',
	'barnlakarforeningen.se': 'Svenska Barnläkarföreningen',
	'nordhemophilia.org': 'Nordic Hemophilia Council',
	'mssallskapet.se': 'MS-sällskapet',
	'njurmed.com': 'Njurmed',
	'infektion.net': 'Infektion.net',
	'swedeye.org': 'Swedeye',
	'd2flujgsl7escs.cloudfront.net': 'Swedeye',
	'huvudvarkssallskapet.se': 'Huvudvärkssällskapet',
	'svenskaenures.se': 'Enuresakademin',
	'skane.se': 'Region Skåne',
	'regionuppsala.se': 'Region Uppsala',
	'slf.se': 'Sveriges Läkarförbund',
	'sls.se': 'Svenska Läkaresällskapet',
}

/**
 * Hosts that bundle multiple specialist organisations under a single domain,
 * where the hostname alone collapses different orgs into the parent label.
 * For chunks served from these hosts, we read the chunk title prefix instead
 * (the upload pipeline writes "<ORG> - <doc> - <section>" in the CSV title column).
 */
const MULTI_ORG_HOSTS = new Set(['forening.sls.se', 'slf.se'])

/**
 * Title-based overrides that win over hostname. Used when one of our curated
 * sources (e.g. Skånelistan) ingests references to PDFs hosted on third-party
 * domains we have NOT included as standalone sources. Without these the pill
 * would imply the chunk came from `eped.se` / `socialstyrelsen.se` / etc. —
 * misleading both about provenance and about which sources we trust.
 */
const TITLE_PREFIX_OVERRIDES: Array<{ prefix: string; name: string }> = [
	{ prefix: 'Skånelistan', name: 'Region Skåne' },
]

function findTitleOverride(title: string): string | null {
	for (const o of TITLE_PREFIX_OVERRIDES) {
		// Match "<prefix> " or "<prefix>-" so we don't false-match e.g. a doc
		// titled "Skånelistans historia" if it appeared in another source.
		if (title.startsWith(o.prefix + ' ') || title.startsWith(o.prefix + '-')) {
			return o.name
		}
	}
	return null
}

/**
 * Title-prefix → display-name overrides for multi-org chunks.
 * Used to restore Swedish characters lost during ASCII-only CSV upload
 * (e.g. "Forening" → "Förening").
 */
const TITLE_PREFIX_NORMALIZATION: Record<string, string> = {
	'Svensk Barnkirurgisk Forening': 'Svensk Barnkirurgisk Förening',
	'Svensk Thoraxkirurgisk Forening': 'Svensk Thoraxkirurgisk Förening',
	'Svensk Forening for Thoraxradiologi': 'Svensk Förening för Thoraxradiologi',
}

/**
 * Organisations whose upload titles merge the abbreviation with a topic
 * (e.g. "RAV Influensa", "RAV Covid-19", "RAV Okategoriserad"). For those
 * we want the pill to show just the abbreviation, not the topic.
 * Two-token abbreviations like "SVKF GUCH" do NOT belong here.
 */
const FIRST_WORD_ONLY_ABBREVIATIONS = new Set(['RAV'])

function extractTitlePrefix(title: string): string | null {
	// Split on " - " (hyphen) or " – " (en-dash), both with spaces.
	// The capture group can never end with whitespace because the regex
	// consumes the trailing `\s` outside the group.
	const match = title.match(/^(.+?)\s[-–]\s/)
	const prefix = match ? match[1]! : null
	if (!prefix) return null
	const firstWord = prefix.split(' ')[0]
	if (firstWord && FIRST_WORD_ONLY_ABBREVIATIONS.has(firstWord)) {
		return firstWord
	}
	return TITLE_PREFIX_NORMALIZATION[prefix] ?? prefix
}

export function getSourceName(url: string, title?: string): string {
	try {
		const hostname = new URL(url).hostname.replace(/^www\./, '')

		// Title-based overrides run first — they handle cases where the URL
		// host doesn't match any of our curated sources (e.g. Skånelistan
		// chunks that link to off-host PDFs on eped.se, socialstyrelsen.se).
		if (title) {
			const override = findTitleOverride(title)
			if (override) return override
		}

		// Multi-org hosts collapse to the parent organisation if we use
		// hostname alone — read the chunk title prefix instead.
		if (title && MULTI_ORG_HOSTS.has(hostname)) {
			const prefix = extractTitlePrefix(title)
			if (prefix) return prefix
		}

		for (const [domain, name] of Object.entries(SOURCE_NAMES)) {
			if (hostname === domain || hostname.endsWith('.' + domain)) {
				return name
			}
		}
		const parts = hostname.split('.')
		return parts.length >= 2 ? parts[parts.length - 2] : hostname
	} catch {
		return 'Källa'
	}
}

/**
 * Display names for the corpus codes the self-built engine puts in each
 * source's `source` field (Typesense `source`, = `short_name` in the content
 * pipeline's sources/<id>/source.yaml). Codes not listed are shown as-is
 * ("RCC", "VISS", "SFOG", "RAF"). Values match SOURCE_NAMES where both apply:
 * click-out tracking reports the name as `destination_source`, and partner
 * reports want the canonical one (plausible.client.ts, trackClickOut docs).
 *
 * Why the code and not the URL: the hostname logic above relies on an
 * "<ORG> - …" title prefix for shared hosts (slf.se, forening.sls.se), which
 * the old upload titles carried. Typesense titles don't, so about 1 700
 * external chunks (RAF, RAV, SFBUP, SURF, SRF, Infektion.net on
 * qrora.com …) would get a title fragment or a bare domain as their name
 * (measured 2026-09-14). The code is the identity the index actually carries.
 * Established abbreviations stay as they are (SFOG, SGF, SRF, RAF …), like
 * SOURCE_NAMES does; spelled-out entries are the ones users don't know.
 * Long-term home: a NEW `display_name` per source in the pipeline (not its
 * `name`, which is longer than these), indexed on both collections, and then
 * this table goes — together with the Snabbsök card's own code-keyed tables
 * (SHORT_SOURCE_NAMES / SOURCE_FULL_NAMES in search-lib.ts, a different
 * register: terse block labels and hover-long names). Until then the
 * pipeline's `short_name` is a cross-repo contract: a renamed code shows up
 * raw here.
 */
export const SOURCE_CODE_NAMES: Record<string, string> = {
	// ASCII codes → å/ä/ö, same three as the title-prefix normalisation above
	...TITLE_PREFIX_NORMALIZATION,
	BLF: 'Svenska Barnläkarföreningen',
	LMB: 'Läkemedelsboken',
	RMR: 'RMR VGR',
	LV: 'Läkemedelsverket',
	FoHM: 'Folkhälsomyndigheten',
	'SoS FMB': 'Socialstyrelsen',
	'SoS Riktlinjer': 'Socialstyrelsen',
	HVS: 'Huvudvärkssällskapet',
	SNEMA: 'Svenska Neuromuskulära Arbetsgruppen',
	SVOS: 'Svenska Osteoporossällskapet',
	SWEMODIS: 'Swedish Movement Disorder Society',
	SFFA: 'Svenska föreningen för Allergologi',
	'Nord Hemophilia': 'Nordic Hemophilia Council',
	Skånelistan: 'Region Skåne',
	Endokrinologforeningen: 'Svensk Endokrinologförening',
	'Svensk Urologisk Forening': 'Svensk Urologisk Förening',
	'Svensk ONH-forening': 'Svensk ONH-förening',
	'Svensk Forening for Beroendemedicin': 'Svensk Förening för Beroendemedicin',
}

/**
 * Name shown for one entry of the chat's `sources` frame. Chip, hovercard and
 * footer all use this, so they agree. Entries are named from their corpus code
 * (`source`); an entry whose index record has no code falls back to the
 * URL/title-based name.
 */
export function sourceDisplayName(source: {
	url: string
	title?: string
	source?: string
}): string {
	const code = source.source
	if (code) return SOURCE_CODE_NAMES[code] ?? code
	return getSourceName(source.url, source.title)
}

/**
 * Spelled-out name for the sources footer and the hovercard, where there is
 * room and no expanded card to lean on: "Referensgruppen för Antibiotikafrågor"
 * rather than "RAF". Chips in the running text and click-out analytics keep the
 * short register (sourceDisplayName). The long names are the Snabbsök card's
 * hover table (SOURCE_FULL_NAMES in search-lib.ts, keyed by the same codes);
 * a code missing there falls back to the short name. An entry without a code
 * falls back to the URL/title-based name.
 */
export function sourceLongName(source: {
	url: string
	title?: string
	source?: string
}): string {
	const code = source.source
	if (!code) return getSourceName(source.url, source.title)
	const full = sourceFullName(code)
	return full !== code ? full : sourceDisplayName(source)
}

export function isInternetmedicin(url: string): boolean {
	try {
		const hostname = new URL(url).hostname.replace(/^www\./, '')
		return (
			hostname === 'internetmedicin.se' ||
			hostname.endsWith('.internetmedicin.se')
		)
	} catch {
		return false
	}
}

/**
 * Chip colour (hex, white text, AA) for an entry in the chat's sources frame,
 * sent to the site's AI box as `color` (IM-82). Same palette as the Snabbsök
 * card (sourceColor); the assistant's own chat chips don't use it (they stay
 * green = IM / orange = external). Internetmedicin is decided by URL, the same
 * isInternetmedicin check those chips use, so IM's lowercase corpus code can't
 * hash into the ring; an external URL never gets the IM green, even if its
 * code says Internetmedicin. Green is reserved for Internetmedicin.
 */
export function sourceChipColor(source: {
	url: string
	source?: string
}): string {
	if (isInternetmedicin(source.url)) return sourceColor('Internetmedicin')
	return sourceColor(
		source.source === 'Internetmedicin' ? undefined : source.source,
	)
}
